import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuCalendar, LuClipboardList, LuFolderKanban, LuHospital, LuHouse, LuInfo, LuTriangleAlert, LuUserRound } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { EmptyState, SearchField } from '../ProfilePage/components/ProfileUi';
import useSmoText from './smoText';

// Turkish labels for the issueCategory codes used in the visit evaluation form.
// The in-modal search matches on these (as before); the screen shows the
// translated label.
const ISSUE_CATEGORY_LABELS = {
  iv_access: 'IV Erişim',
  patient_compliance: 'Hasta Uyumu',
  documentation: 'Dokümantasyon',
  logistics: 'Lojistik',
  protocol: 'Protokol',
  equipment: 'Ekipman',
  other: 'Diğer',
};

// Each "flag" describes one kind of problem a visit can carry. We render a chip
// for every flag that is active on a visit so the SMO can see, at a glance,
// what went wrong.
const FLAGS = [
  { key: 'issueOccurred', tone: 'danger' },
  { key: 'issueResultedInMiss', tone: 'danger' },
  { key: 'protocolDeviation', tone: 'warning' },
  { key: 'capaOpened', tone: 'warning' },
  { key: 'screenFail', tone: 'warning' },
  { key: 'dropPatient', tone: 'warning' },
  { key: 'wasAeSaeTaken', tone: 'info' },
  { key: 'dcfCreated', tone: 'info' },
];

const FLAG_TONES = {
  danger: 'tw-bg-rose-500/15 tw-text-rose-800 dark:tw-text-rose-200',
  warning: 'tw-bg-amber-400/20 tw-text-amber-800 dark:tw-text-amber-200',
  info: 'tw-bg-sky-500/15 tw-text-sky-800 dark:tw-text-sky-200',
};

const isOverdue = (v) => {
  const ev = v.visitEvaluation || {};
  const start = v.date || v.firstVisitDate;
  if (!ev.slaTarget || !ev.processEndDate || !start) return false;
  const cycle = Math.ceil((new Date(ev.processEndDate) - new Date(start)) / 86400000);
  return cycle > parseFloat(ev.slaTarget);
};

/**
 * Drill-down opened from any clicked issue number on the page: which visits
 * are behind it, who made them, when, and what went wrong.
 */
const IssueDetailModal = ({ show, onHide, title, visits = [], workers = [], projectNames = {} }) => {
  const { t, label, language } = useSmoText();
  const [search, setSearch] = useState('');

  const coordName = (v) => {
    const w = workers.find((wk) => wk.id === v.employeeId);
    return w ? `${w.name} ${w.surname}` : (v.employeeName || v.employeeId || label('issueModal.unknownWorker'));
  };

  // Resolve the human-readable short project title, falling back to any name
  // stored on the visit, and only then to the raw id.
  const projectName = (v) =>
    projectNames[v.projectId] || v.projectName || v.projectId || '';

  // Sort newest-first, then apply the in-modal search filter.
  const rows = useMemo(() => {
    const enriched = (visits || []).map((v) => ({
      visit: v,
      who: coordName(v),
      when: v.date || v.firstVisitDate || '',
      project: projectName(v),
    }));
    enriched.sort((a, b) => (b.when || '').localeCompare(a.when || ''));
    const q = search.trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter((r) => {
      const ev = r.visit.visitEvaluation || {};
      const cat = ISSUE_CATEGORY_LABELS[ev.issueCategory] || ev.issueCategory || '';
      return (
        r.who.toLowerCase().includes(q) ||
        String(r.project).toLowerCase().includes(q) ||
        cat.toLowerCase().includes(q) ||
        String(ev.issueDetails || '').toLowerCase().includes(q) ||
        String(ev.comments || '').toLowerCase().includes(q) ||
        String(ev.patientId || r.visit.patientId || '').toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, workers, search]);

  const categoryText = (code) => (ISSUE_CATEGORY_LABELS[code] ? label(`issueModal.categories.${code}`) : code);

  return (
    <Modal
      isOpen={!!show}
      onClose={onHide}
      size="lg"
      title={title}
      subtitle={label('issueModal.count', { count: rows.length })}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-600 tw-shadow-amber-500/30" size="xs">
          <LuTriangleAlert aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button onClick={onHide}>{t('common.close')}</Button>}
    >
      <div className="tw-space-y-3">
        <SearchField value={search} onChange={setSearch} placeholder={label('issueModal.search')} />
        {rows.length === 0 ? (
          <EmptyState icon={LuClipboardList} title={label('issueModal.empty')} />
        ) : (
          <ul className="tw-space-y-2.5">
            {rows.map((r, idx) => {
              const v = r.visit;
              const ev = v.visitEvaluation || {};
              const overdue = isOverdue(v);
              const isHome = v.visitType === 'home';
              const activeFlags = FLAGS.filter((f) => ev[f.key] === 'yes');
              const category = ev.issueCategory ? categoryText(ev.issueCategory) : null;
              const patientId = ev.patientId || v.patientId;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={v.id || idx} className="tw-space-y-2.5 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  {/* Card top: who + when */}
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
                      <LuUserRound aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-sky-500" />
                      {r.who}
                    </span>
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">
                      <LuCalendar aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                      {r.when || label('issueModal.noDate')}
                    </span>
                  </div>

                  {/* Meta row: visit type, project, patient */}
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs">
                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-font-semibold ${isHome ? 'tw-bg-sky-500/15 tw-text-sky-800 dark:tw-text-sky-200' : 'tw-bg-violet-500/15 tw-text-violet-800 dark:tw-text-violet-200'}`}>
                      {isHome ? <LuHouse aria-hidden="true" className="tw-h-3 tw-w-3" /> : <LuHospital aria-hidden="true" className="tw-h-3 tw-w-3" />}
                      {isHome ? label('tasks.homeVisit') : label('tasks.siteVisit')}
                    </span>
                    {r.project && (
                      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-slate-600 dark:tw-text-slate-300">
                        <LuFolderKanban aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />
                        {r.project}
                      </span>
                    )}
                    {patientId && (
                      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-slate-600 dark:tw-text-slate-300">
                        <LuInfo aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />
                        {label('issueModal.patient', { id: patientId })}
                      </span>
                    )}
                  </div>

                  {/* Problem flags */}
                  {(activeFlags.length > 0 || overdue) && (
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                      {activeFlags.map((f) => (
                        <span key={f.key} className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${FLAG_TONES[f.tone]}`}>
                          {label(`issueModal.flags.${f.key}`)}
                        </span>
                      ))}
                      {overdue && <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${FLAG_TONES.danger}`}>{label('issueModal.flags.overdue')}</span>}
                    </div>
                  )}

                  {/* What: category + details */}
                  {(category || ev.issueDetails || ev.comments) && (
                    <dl className="tw-space-y-1.5 tw-rounded-xl tw-bg-slate-500/[0.05] tw-p-3 tw-text-sm dark:tw-bg-white/[0.03]">
                      {category && (
                        <div className="tw-grid tw-gap-1 sm:tw-grid-cols-[10rem_1fr]">
                          <dt className="tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('issueModal.category')}</dt>
                          <dd className="tw-text-slate-800 dark:tw-text-slate-100">{category}</dd>
                        </div>
                      )}
                      {ev.issueDetails && (
                        <div className="tw-grid tw-gap-1 sm:tw-grid-cols-[10rem_1fr]">
                          <dt className="tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('issueModal.details')}</dt>
                          <dd className="tw-break-words tw-text-slate-800 dark:tw-text-slate-100">{ev.issueDetails}</dd>
                        </div>
                      )}
                      {ev.comments && (
                        <div className="tw-grid tw-gap-1 sm:tw-grid-cols-[10rem_1fr]">
                          <dt className="tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('issueModal.notes')}</dt>
                          <dd className="tw-break-words tw-text-slate-800 dark:tw-text-slate-100">{ev.comments}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};

IssueDetailModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
  visits: PropTypes.array,
  workers: PropTypes.array,
  projectNames: PropTypes.object,
};

export default IssueDetailModal;
